import { useProvider } from '@newfront-insurance/react-provision';
import { useCallback, useMemo } from 'react';

import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import { createNovaTransport, probePersistedDurableActiveStream } from '../lib/transport';
import { useNovaContext } from '../providers';

interface UseChatTransportOptions {
  onReconnectUnavailable?: (chatUuid: string) => void;
  onWorkflowAttached?: (attachment: { chatUuid: string; workflowRunId: string }) => void;
}

/**
 * Hook to create and memoize the transport for the chat's backend.
 * The transport is recreated when context values change.
 */
export function useChatTransport({ onReconnectUnavailable, onWorkflowAttached }: UseChatTransportOptions = {}) {
  const { chatBackend } = useChatOverlayContext();
  const { userUuid, accountUuid, authProvider } = useNovaContext();
  const { getToken } = useProvider(authProvider);

  const transport = useMemo(() => {
    return createNovaTransport({
      backend: chatBackend,
      getToken,
      onReconnectUnavailable,
      onWorkflowAttached,
      userUuid,
      accountUuid,
    });
  }, [accountUuid, chatBackend, getToken, onReconnectUnavailable, onWorkflowAttached, userUuid]);

  return transport;
}

export function usePersistedDurableActiveStreamProbe() {
  const { authProvider } = useNovaContext();
  const { getToken } = useProvider(authProvider);

  return useCallback(
    ({ chatUuid, signal, workflowRunId }: { chatUuid: string; signal?: AbortSignal; workflowRunId: string }) =>
      probePersistedDurableActiveStream({
        chatUuid,
        getToken,
        signal,
        workflowRunId,
      }),
    [getToken],
  );
}
